import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useConfirmOnEnter } from '@/features/Quote/hooks/useConfirmOnEnter';
import type { SettingType } from '@/types/app';

interface StepConfirmButtonProps {
	/** Next unconfirmed wizard step, or null when this card is the terminal step. */
	nextStep: SettingType | null;
	/** Confirm this card and move on: receives the next step, or null to finish. */
	onConfirm: (target: SettingType | null) => void;
	disabled?: boolean;
	loading?: boolean;
	className?: string;
	/** Employees uses a shorter confirm label on small screens. */
	compactConfirmLabel?: boolean;
	/**
	 * When advancing to another step, show "Bevestigen" instead of "Volgende".
	 * Used by the absence card; employees keeps the default "Volgende".
	 */
	confirmAsNextLabel?: boolean;
}

/**
 * Shared confirmation-wizard button used by every settings card. Shows "Volgende"
 * (or "Bevestigen" when `confirmAsNextLabel`) while another warning still needs
 * confirming, otherwise "Bevestigen en herberekenen" (the click that triggers the
 * deferred recalculation). The data-testids (`next-button` / `submit-button`) are
 * relied upon by tests.
 */
const StepConfirmButton = ({
	nextStep,
	onConfirm,
	disabled = false,
	loading = false,
	className = 'w-full',
	compactConfirmLabel = false,
	confirmAsNextLabel = false
}: StepConfirmButtonProps) => {
	const { t } = useTranslation();

	const handleConfirm = useCallback(() => onConfirm(nextStep), [onConfirm, nextStep]);

	useConfirmOnEnter({
		disabled: disabled || loading,
		onConfirm: handleConfirm
	});

	if (nextStep) {
		return (
			<Button
				type="button"
				size="sm"
				variant="success"
				className={className}
				onClick={handleConfirm}
				disabled={disabled}
				data-testid="next-button"
			>
				{t(confirmAsNextLabel ? 'quotes.settings.buttons.confirm' : 'quotes.settings.buttons.next')}
			</Button>
		);
	}

	return (
		<Button
			type="button"
			size="sm"
			variant="success"
			className={className}
			onClick={handleConfirm}
			disabled={disabled}
			loading={loading}
			data-testid="submit-button"
		>
			{compactConfirmLabel ? (
				<>
					<span className="hidden sm:inline">
						{t('quotes.settings.buttons.confirmAndRecalculate')}
					</span>
					<span className="sm:hidden">{t('quotes.settings.buttons.confirm')}</span>
				</>
			) : (
				t('quotes.settings.buttons.confirmAndRecalculate')
			)}
		</Button>
	);
};

export default StepConfirmButton;
